import { visibleTrend, trendEmptyMessage } from '../trendPeriods'
import { PartialPeriods } from './PartialPeriods'
import { useAssortment } from '../useAssortment'
import { useRequestState } from '../useRequestState'
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Pin } from 'lucide-react'
import { computeInsights, type Insight } from '../insights'
import { defaultSelection, isOwn, orderedSelection } from '../brandSelection'
import { largestBrand, largestBrandText } from '../largestBrand'
import { mainSegmentLabel, mainSegmentShareText, segmentMarketScope, shareCardFigure, type ShareCardFigure } from '../mainSegmentShare'
import { ArticleComparison } from './ArticleComparison'
import { BrandPicker } from './BrandPicker'
import { ScrollRow } from './ScrollRow'
import { SearchableSelect } from './SearchableSelect'
import { StatusText } from './StatusText'
import { InsightChart } from '../charts/InsightChart'
import { isIncompletePoint, missingDays } from '../charts/partial'
import { TrendNotes } from './TrendNotes'
import type { Annotation } from '../api/annotations'
import {
  FREQ_LABEL,
  downloadArticlesExport,
  fetchBrands,
  fetchDosLayer,
  fetchDosTrend,
  fetchProductFilters,
  type ProductFacet,
  type ProductFilterOptions,
  fetchMarketShareTrend,
  ROLLING_WINDOW,
  transformSeries,
  windowRange,
  bucketRange,
  isoMinusDays,
  noteBucketMap,
  windowPrevRange,
  prevRangeMode,
  formatRangeLabel,
  usableWindowPresets,
  WINDOW_UNIT,
  defaultWindowN,
  availableTrendUnits,
  indexSeries,
  mergeTrends,
  prevPeriodOf,
  throughLabel,
  sumSeries,
  type AttributeOption,
  type BrandOption,
  type DosLayer,
  type Freq,
  mainSegmentOf,
  UNRESOLVED_BRAND_LABEL,
  type Period,
  type TrendWindow,
} from '../landing'
import {
  SEGMENTS,
  segmentApiValue,
  segmentDenominatorLabel,
  segmentLabel,
  type Segment,
} from '../segments'
import { displayLabel, isLooseSnus } from '../displayLabels'
import { brand, formatNumber } from '../theme'
import { changeDirection, formatPercent, formatShare, MINUS } from '../format'
import { ACTIVE_SUPPLIER, TENANT } from '../tenant'
import { loadTrendFilters, sanitizeTrendView, saveTrendFilters, trendFilterKey, type TrendViewState } from '../trendFilters'
import { trendChartTitles, trendSeriesPlan } from '../trendSeries'
import { exportMonthlyReport, exportTrendCsv, type MonthlyReport } from '../util/exporter'
import { REPORT_TREND_UNAVAILABLE, REPORT_TREND_UNAVAILABLE_PDF, fetchMonthlyReportTrend, monthlyReportData } from '../util/monthlyReportData'
import { buildExportFilename, exportScope, REPORT_TYPES } from '../util/exportFilename'
import { exportFilters } from '../util/exportSelection'
import { COVERAGE_THRESHOLD, chartCoverageSources, coveragePercentText, coverageSelectionRows, coverageWarnings, unbucketedLine } from '../util/attributeCoverage'
import { SKIPPED, useDownload } from '../useDownload'
import { DownloadError } from './DownloadError'
import type { Chart, ChartDatum } from '../types'
import { InfoTip } from './InfoTip'
import { facitBadge } from '../facitBadge'
import { useStickyOffset } from '../useStickyOffset'
import { ambiguousNames, articleName, isUnnamed } from '../missingLabels'
import { NO_SALES_FOR_SELECTION, NO_SALES_MARK, optionText, salesScopeKey, selectedWithoutSales } from '../filterSales'

// Scope name for labels — the manufacturer's DISPLAY NAME (not its raw scope value, which
// for a non-onboarded manufacturer is a source id like "202" or "320,129"), or "The market"
// in the whole-market view, so labels never render with an orphan leading "·".
const SCOPE = TENANT.manufacturer ? ACTIVE_SUPPLIER.label : 'The market'
const EXPORT_SCOPE = exportScope(TENANT.manufacturer, ACTIVE_SUPPLIER.label) // file names (MP-OS-190)
// The admin "Alla märken" profile: own ≡ market, so an aggregate market share is 100 %.
const WHOLE_MARKET = !TENANT.manufacturer

// Eget intervall spänner max 52 veckor (se render). UTC-räkning för att undvika DST-glidning.
const MAX_CUSTOM_SPAN_DAYS = 364
const clampMax = (iso: string, min: string): string => (iso < min ? min : iso)

// MP-OS-190 — a hung PDF capture (e.g. html-to-image never settling) must not leave the
// button stuck in "Generating…" forever. A client-side time budget forces an error state
// and re-enables retry even if the underlying promise never resolves or rejects.
const REPORT_TIMEOUT_MS = 20000
const REPORT_SUCCESS_MS = 1600


const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

// Count a KPI up from 0 to its value, once (the landing sequence). When not
// playing it shows the final value immediately — export/refresh never animate.
// `count` defaults true (additive counts ramp up). Pass count={false} for ratios/%:
// a bounded share sweeping 0→23,4 % reads as a real (wrong) value mid-animation.
function Num({ value, play, fmt, count = true }: { value: number; play: boolean; fmt: (n: number) => string; count?: boolean }) {
  const [n, setN] = useState(play && count ? 0 : value)
  useEffect(() => {
    if (!play || !count) {
      setN(value)
      return
    }
    let raf = 0
    const t0 = performance.now()
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / 650)
      setN(value * (1 - Math.pow(1 - p, 3))) // easeOutCubic
      if (p < 1) raf = requestAnimationFrame(tick)
      else setN(value)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [value, play])
  return <>{fmt(n)}</>
}

// STG landing header. DOSOR (validated dos-layer) for KPIs + All White market
// share; FÖRPACKNINGAR (cube, real daily) for the time-series hero — clearly
// labelled, since a daily dosor series needs the dated nightly ETL. No number is
// ever shown in an unlabelled/wrong unit (the demo-1 failure).

// Solvigo DS: sharp edges (radius ≤4px), weight ≤600, hierarchy from size/weight.
// Use the themeable radius tokens so controls/cards follow the tenant's softness
// (MinPrilla overlay rounds these; the Solvigo default stays sharp).
const RC = 'var(--radius-card)' // cards
const RB = 'var(--radius-btn)' // controls / toggles / tags / bars

const S: Record<string, CSSProperties> = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 20 },
  kpi: { flex: '1 1 190px', minWidth: 178, background: brand.surface, border: `1px solid ${brand.grey}`, borderRadius: RC, padding: '12px 14px' },
  kpiLabel: { fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: brand.greyText },
  // Signature: large Jost tabular numerals; hierarchy from size/weight (SemiBold,
  // never heavier — Solvigo DS), the number IS the design.
  kpiValue: { fontFamily: brand.fontDisplay, fontSize: 'var(--fs-h2)', fontWeight: 600, color: brand.tealDark, lineHeight: 1.1, marginTop: 8, fontVariantNumeric: 'tabular-nums' },
  kpiDelta: { fontSize: 12.5, marginTop: 6, fontVariantNumeric: 'tabular-nums', display: 'flex', alignItems: 'baseline', gap: 6 },
  kpiSnap: { fontSize: 11.5, color: brand.greyText, fontWeight: 600 },
  // MPD-68: an honest empty state in the figure's place ("No sales in September 2026"), never 0 %.
  kpiEmpty: { fontSize: 'var(--fs-body)', color: brand.greyText, fontWeight: 600 },
  // A figure from a period other than the selected one, held in place while the new one
  // loads: legible but visibly provisional, never mistakable for a settled number.
  kpiValueStale: { opacity: 0.4 },
  kpiUpdating: { fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', color: brand.greyText, marginTop: 4 },
  kpiNote: { fontSize: 11.5, color: brand.greyText, marginTop: 5 },
  // Flat status bar for a bounded 0–100 % measure (All White share): one accent fill on a
  // light track. No skeuomorphic gauge, no traffic-light zones — the fill length IS the read.
  kpiBarTrack: { marginTop: 10, height: 5, borderRadius: 999, background: brand.grey, overflow: 'hidden' as const },
  kpiBarFill: { height: '100%', borderRadius: 999, background: brand.tealDark },
  card: { background: brand.surface, border: `1px solid ${brand.grey}`, borderRadius: RC, padding: '14px 18px 10px' },
  // Two charts side by side carry different amounts of control chrome above them, so laying
  // them out top-down put their x-axes on different lines. The card is a column and the chart
  // is pushed to its bottom edge (`chartSlot`); grid stretch makes the cards equally tall, so
  // both x-axes land on the same line whatever sits above them.
  chartCard: { display: 'flex' as const, flexDirection: 'column' as const },
  chartSlot: { marginTop: 'auto' as const },
  head: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 },
  title: { fontSize: 15, fontWeight: 600, color: brand.ink, marginRight: 'auto' },
  tag: { fontSize: 10.5, fontWeight: 600, color: brand.greyText, background: 'var(--bg-muted)', border: `1px solid ${brand.grey}`, padding: '3px 8px', borderRadius: RB },
  seg: { display: 'inline-flex', flexWrap: 'wrap', maxWidth: '100%', background: brand.mint, border: `1px solid ${brand.grey}`, borderRadius: RB, padding: 3, gap: 2 },
  // The segment pills: S.seg as one row that never wraps or shrinks; ScrollRow scrolls it.
  segRow: { display: 'inline-flex', flexWrap: 'nowrap', flex: 'none', background: brand.mint, border: `1px solid ${brand.grey}`, borderRadius: RB, padding: 3, gap: 2 },
  chip: { border: `1px solid ${brand.grey}`, background: brand.surface, borderRadius: RB, padding: '6px 12px', fontWeight: 600, fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit' },
  rangeCaption: { fontSize: 11.5, color: brand.greyText, margin: '2px 0 8px' },
  pickGroup: { display: 'inline-flex', flexWrap: 'wrap', maxWidth: '100%', alignItems: 'center', gap: 6 },
  pickLabel: { fontSize: 12, fontWeight: 600, color: brand.greyText },
  dateInput: { fontFamily: 'inherit', fontSize: 12.5, padding: '5px 8px', borderRadius: RB, border: `1px solid ${brand.grey}`, background: brand.surface, color: brand.ink, cursor: 'pointer' },
  dateArrow: { color: brand.greyText, fontSize: 13 },
  empty: { padding: '40px 0', textAlign: 'center', color: brand.greyText, fontSize: 13 },
  // Look comes from .control-select (MP-OS-191 F); only the slightly taller trend-bar padding stays.
  sel: { padding: '6px 8px' },
  scRow: { display: 'grid', gridTemplateColumns: '1fr auto auto', alignItems: 'baseline', gap: 12, padding: '9px 0', borderTop: `1px solid ${brand.grey}` },
  scLabel: { fontSize: 13, color: brand.greyText, fontWeight: 600 },
  scVal: { fontSize: 16, fontWeight: 600, color: brand.tealDark, fontVariantNumeric: 'tabular-nums', fontFamily: brand.fontDisplay, justifySelf: 'end' },
  scNote: { fontSize: 11.5, color: brand.greyText, justifySelf: 'end', textAlign: 'right' as const },
}

// Period-delta pill — ▲/▼ + %, coloured by MEANING only (green up / red down,
// never the brand accent). Compact = % only (KPI tiles); full = absolute + %
// (scorecard). Only rendered where a comparable previous period exists.
const UP = '#1f9d57'
const DOWN = '#c2453a'
// MPD-132: a share the source only sends for a row with sales (the own share card, the brand
// ranking): one that rounds to 0 reads "<0.1 %", never "0.0 %".
const soldShare = (n: number): string => formatShare(n, { hasSales: true })

// MP-OS-206: colour, arrow and the percent's sign follow the DISPLAYED percent, so a change
// that rounds to 0.0 % is grey with no arrow and no sign. The absolute figure keeps its own sign.
export function Delta({ abs, pct, compact }: { abs: number; pct: number; compact?: boolean }) {
  const dir = changeDirection(pct)
  const flat = dir !== 'up' && dir !== 'down'
  const pctText = flat ? formatPercent(0) : `${dir === 'up' ? '+' : MINUS}${formatPercent(Math.abs(pct))}`
  const absText = `${abs > 0 ? '+' : abs < 0 ? MINUS : ''}${formatNumber(Math.abs(abs))}`
  const body = compact ? pctText : `${absText} (${pctText})`
  return (
    <span style={{ color: flat ? brand.greyText : dir === 'up' ? UP : DOWN, fontWeight: 600 }} className="tnum">
      {flat ? body : `${dir === 'up' ? '▲' : '▼'} ${body}`}
    </span>
  )
}

function segBtn(active: boolean): CSSProperties {
  // Active segment in brand petrol (not black) — on-brand + AA contrast with white.
  return { border: 0, background: active ? brand.tealDark : 'transparent', color: active ? '#fff' : brand.greyText, fontWeight: 600, fontSize: 12.5, padding: '6px 13px', borderRadius: RB, cursor: 'pointer', fontFamily: 'inherit' }
}
function chipOn(active: boolean): CSSProperties {
  return { ...S.chip, background: active ? brand.mint : brand.surface, color: active ? brand.tealDark : brand.greyText, borderColor: active ? brand.teal : brand.grey }
}

function Kpi({
  label,
  value,
  delta,
  note,
  bar,
  rise,
  delay = 0,
  stale = false,
}: {
  label: ReactNode
  value: ReactNode
  delta?: ReactNode // delta vs föregående period, or an honest snapshot/missing line
  note?: ReactNode // etikettrad: nämnare + enhet + källa, with recovery controls on failure
  bar?: number | null // 0–100 % bounded measure → a flat status bar (grounded value, no target)
  rise?: boolean
  delay?: number
  // The rendered figure belongs to a period other than the selected one — it is kept in
  // place so the card doesn't collapse, but it must never read as this period's final
  // number. Dimmed + aria-busy, with the caption saying so.
  stale?: boolean
}) {
  return (
    <div
      style={{ ...S.kpi, ...(rise ? { animationDelay: `${delay}ms` } : {}) }}
      className={rise ? 'lh-rise' : undefined}
      aria-busy={stale || undefined}
    >
      <div style={S.kpiLabel}>{label}</div>
      <div style={{ ...S.kpiValue, ...(stale ? S.kpiValueStale : {}) }}>{value}</div>
      {stale && <div style={S.kpiUpdating}>updating…</div>}
      {delta && <div style={S.kpiDelta}>{delta}</div>}
      {bar != null && (
        <div style={S.kpiBarTrack} aria-hidden>
          <div style={{ ...S.kpiBarFill, width: `${Math.max(0, Math.min(100, bar))}%` }} />
        </div>
      )}
      {note && <div style={S.kpiNote}>{note}</div>}
    </div>
  )
}

// MP-OS-50 — accumulated with a "ghost" of the previous comparable period behind it.
// Both series are accumulated and aligned by POSITION (vecka 1, 2, …), since the two
// periods have different absolute week labels. series[1] renders dashed/subordinate.
// Align the current window against its comparison window BY POSITION (bucket 1, 2, …),
// since the two spans carry different absolute labels. Values stay raw here — Ackumulerat
// and Rullande medel are applied afterwards by transformSeries, which covers every series.
// That keeps the transforms in one place and makes the comparison usable with or without
// cumulative (A7).
export function withGhost(cur: Chart, prev: Chart, curLabel: string, prevLabel: string): Chart {
  const valOf = (rows: ChartDatum[]) => rows.map((r) => typeof r.value === 'number' && Number.isFinite(r.value) ? r.value : null)
  const curVals = valOf(cur.data)
  const prevVals = valOf(prev.data)
  const n = Math.max(curVals.length, prevVals.length)
  const data: ChartDatum[] = []
  for (let i = 0; i < n; i++) {
    const row: ChartDatum = {
      name: cur.data[i]?.name ?? prev.data[i]?.name ?? String(i + 1),
      [curLabel]: curVals[i] ?? null,
      [prevLabel]: prevVals[i] ?? null,
    }
    for (const key of ['period_from', 'period_to', 'period_kind']) {
      const value = cur.data[i]?.[key]
      if (value != null) row[key] = value
    }
    if (prev.data[i]?.period_kind === 'partial') row.period_kind = 'partial'
    // MP-OS-207: an incomplete period in either year marks the row incomplete.
    const incomplete = [cur.data[i], prev.data[i]].filter(isIncompletePoint)
    if (incomplete.length) {
      row.coverage = 'incomplete'
      row.missing_days = Math.max(...incomplete.map(missingDays))
    }
    data.push(row)
  }
  return { type: 'line', unit: cur.unit, ref: null, series: [curLabel, prevLabel], data }
}

function LandingHeroInner({
  onPinInsight,
  pinnedInsightIds,
  periods,
  period,
  view = 'overview',
  username,
  onShareCard,
}: {
  onPinInsight: (ins: Insight) => void
  pinnedInsightIds: Set<string>
  periods: Period[]
  period: Period
  view?: 'overview' | 'trends'
  username?: string
  onShareCard?: (figure: ShareCardFigure | null) => void
}) {
  const assortment = useAssortment()
  // MP-OS-188 bug #5 — App.tsx only keeps this component mounted across the Overview↔Trends
  // toggle; navigating to any other section (Top lists, Market, …) and back unmounts it, so
  // every filter below used to silently revert without the user ever pressing Reset. Restore
  // the last saved selection for THIS user and THIS supplier scope — computed once per mount,
  // not re-read on every render — so another account or another "view as supplier" scope never
  // inherits someone else's filters.
  const storageKey = trendFilterKey(username, TENANT.manufacturer)
  const persistedRef = useRef<ReturnType<typeof loadTrendFilters> | undefined>(undefined)
  if (persistedRef.current === undefined) persistedRef.current = loadTrendFilters(storageKey)
  const persisted = persistedRef.current
  // MP-OS-188 round 3 — every other control is restored too, each value validated against the
  // data on screen now (a preset the data can't fill, or a custom range outside the history,
  // falls back to the default). Computed once per mount, like the filters above.
  const restoredViewRef = useRef<Partial<TrendViewState> | undefined>(undefined)
  if (restoredViewRef.current === undefined) {
    const end = periods[0]?.through ?? periods[0]?.to ?? period.to
    const start = periods[periods.length - 1]?.from ?? period.from
    restoredViewRef.current = sanitizeTrendView(persisted?.view, {
      dataEnd: end, historyFrom: start, maxCustomSpanDays: MAX_CUSTOM_SPAN_DAYS,
      presetsFor: (f) => usableWindowPresets(f, availableTrendUnits(periods, f, end)),
    })
  }
  const restoredView = restoredViewRef.current
  // start on Week (Olof 4/9: "vi börjar på vecka")
  const [freq, setFreq] = useState<Freq>(() => restoredView.freq ?? 'week')
  const validSegment = (s: string | undefined): Segment | null =>
    s && (SEGMENTS as readonly string[]).includes(s) ? (s as Segment) : null
  // MP-OS-167: the segment and its valid selections are committed together.
  const [segment, setSegment] = useState<Segment>(() => validSegment(persisted?.segment) ?? 'ALL')
  const [requestedSegment, setRequestedSegment] = useState<Segment>(() => validSegment(persisted?.requestedSegment) ?? 'ALL')
  const [filtersLoading, setFiltersLoading] = useState(true)
  const [filtersError, setFiltersError] = useState(false)
  // Still polling past the server's first retry budget (a source outage, MP-OS-167).
  const [filtersSlow, setFiltersSlow] = useState(false)
  const [filterAttempt, setFilterAttempt] = useState(0)
  const segArg = segmentApiValue(segment)
  const [attrs, setAttrs] = useState<{ strength: AttributeOption[]; format: AttributeOption[] }>({ strength: [], format: [] })
  const [facets, setFacets] = useRequestState<ProductFilterOptions | null>(requestedSegment, null)
  const changedFilter = useRef<ProductFacet | undefined>(undefined)
  // MP-OS-201: the selection's CU in the trend window, with the scope it was computed for.
  const [selectionSales, setSelectionSales] = useState<{ key: string; sold: number | undefined } | null>(null)
  const [strength, setStrength] = useState(() => persisted?.strength ?? 'All')
  const [fmt, setFmt] = useState(() => persisted?.fmt ?? 'All')
  const [category, setCategory] = useState(() => persisted?.category ?? 'All')
  const [flavour, setFlavour] = useState(() => persisted?.flavour ?? 'All')
  const [nicotine, setNicotine] = useState(() => persisted?.nicotine ?? 'All')
  const strengthArg = strength === 'All' ? undefined : strength
  const fmtArg = fmt === 'All' ? undefined : fmt
  const extraFilters = { category: category === 'All' ? undefined : category,
    flavour: flavour === 'All' ? undefined : flavour,
    nicotine: nicotine === 'All' ? undefined : nicotine, product_filters_v2: true }
  const extraCaption = [category === 'All' ? 'All' : displayLabel(category), flavour, nicotine === 'All' ? 'All' : `${nicotine} mg/ml`].filter(v => v !== 'All').join(' · ')
  const shareFilters = [strength, fmt].filter(v => v !== 'All').concat(extraCaption ? [extraCaption] : [])

  // Andel-KPI default period (MP-OS-77): the selected month, or all-time ("någonsin").
  const [shareAllTime, setShareAllTime] = useState(false)
  // Dynamic trend window — decoupled from the (facit-certified) KPI/scorecard month.
  // A preset counts back N units of the current granularity; 'custom' is a free range.
  const [win, setWin] = useState<TrendWindow>(() => {
    if (restoredView.win) return restoredView.win
    const end = periods[0]?.through ?? periods[0]?.to ?? period.to
    const f = restoredView.freq ?? 'week'
    return { kind: 'preset', n: defaultWindowN(f, availableTrendUnits(periods, f, end)) }
  })
  const [showCustom, setShowCustom] = useState(() => restoredView.showCustom ?? false)
  const [customRangeError, setCustomRangeError] = useState<string | null>(null)
  useEffect(() => { setCustomRangeError(null) }, [win])
  // Advanced chart overlays fold away by default so Översikt's first glance is the trend,
  // not a wall of controls (Olof: "för mycket info — skala ner"). Segment stays visible.
  const [showAdv, setShowAdv] = useState(() => restoredView.showAdv ?? false)
  const [showPartial, setShowPartial] = useState(() => restoredView.showPartial ?? false)
  // Latest data date the window ends at (the ongoing month's through-date, else its end).
  const dataEnd = periods[0]?.through ?? periods[0]?.to ?? period.to
  // The whole span the data covers (periods are latest-first) — what "någonsin" means for
  // the all-time day record. Derived from the data, so it grows as history deepens.
  const history = { from: periods[periods.length - 1]?.from ?? period.from, to: dataEnd }
  const range = windowRange(win, freq, dataEnd, history.from)
  const prevRange = windowPrevRange(range, prevRangeMode(win, freq))
  // Eget intervall spänner max 52 veckor: en flerårig 'custom' rör ihop veckoserien till
  // en oläsbar gyttja (och drar onödigt tunga trend-queries). Klampas i onChange så en
  // handinmatad datumsträng inte kan gå runt `min`.
  const minCustomFrom = clampMax(isoMinusDays(range.to, MAX_CUSTOM_SPAN_DAYS), history.from)
  // How much history actually exists — so the Tidsspann buttons only offer spans the data
  // can fill differently (6/12/24 mån all show the same 4 points with ~4 mån of data).
  const availUnits: Record<Freq, number> = {
    day: availableTrendUnits(periods, 'day', dataEnd),
    week: availableTrendUnits(periods, 'week', dataEnd),
    month: availableTrendUnits(periods, 'month', dataEnd),
  }
  const presets = usableWindowPresets(freq, availUnits[freq])
  // Switching granularity keeps a custom range verbatim but re-defaults a preset to a
  // sensible count for the new unit — clamped to a span the data can actually show.
  const changeFreq = (f: Freq) => {
    setFreq(f)
    setWin((w) => (w.kind === 'preset' ? { kind: 'preset', n: defaultWindowN(f, availUnits[f]) } : w))
  }
  // MP-OS-188 round 3 — Reset returns every persisted view control to its default as well, so
  // nothing restored from an earlier visit survives a Reset (the segment keeps its own rule).
  const resetView = () => {
    setFreq('week')
    setWin({ kind: 'preset', n: defaultWindowN('week', availUnits.week) })
    setShowCustom(false); setShowPartial(false); setIndexed(false)
    setAcc(false); setAvg(false); setCmpPrev(false); setRollOverride(null); setShowAdv(false)
  }
  // Ackumulerat and Rullande medel are mutually exclusive — cumulating a smoothed curve
  // says nothing (Olof: "det är ju nonsens att ha både"). Each toggle clears the other.
  const [acc, setAcc] = useState(() => restoredView.acc ?? false)
  const [avg, setAvg] = useState(() => restoredView.avg ?? false)
  // A7 — the previous-period series is its own choice now. It used to be implied by
  // Ackumulerat and silently suppressed in per-brand mode, so Olof turned cumulative on and
  // asked where the comparison went: "varför kommer inte den med förra året?".
  const [cmpPrev, setCmpPrev] = useState(() => restoredView.cmpPrev ?? false)
  // Rolling window is adjustable; the default follows granularity (7 dag / 4 vecka / 3 mån).
  const [rollOverride, setRollOverride] = useState<number | null>(() => restoredView.rollOverride ?? null)
  const rollWindow = rollOverride ?? ROLLING_WINDOW[freq]
  // A8.5 — the way out of the scale trap when a small own brand shares an axis with a big
  // competitor. Only meaningful with several series, so it rides with per-märke.
  const [indexed, setIndexed] = useState(() => restoredView.indexed ?? false)
  const [trendAttempt, setTrendAttempt] = useState(0)
  const periodKey = JSON.stringify([TENANT.manufacturer, period.value, period.from, period.to, period.through])
  const trendKey = JSON.stringify([TENANT.manufacturer, range.from, range.to, freq, segment,
    strength, fmt, category, flavour, nicotine, trendAttempt])
  const [hero, setHero] = useRequestState<Chart | null>(trendKey, null)
  const [dos, setDos] = useRequestState<DosLayer | null>(periodKey, null)
  const [loading, setLoading] = useRequestState(trendKey, true)
  // A failed fetch must NOT look like "no data" — that's the trust-killer the DD flags
  // (a silent empty chart is worse than a visible error). Tracked separately.
  const [heroError, setHeroError] = useRequestState(trendKey, false)
  const [dosError, setDosError] = useRequestState(periodKey, false)
  // Orchestrated landing — plays ONCE per session, never under reduced-motion,
  // and never affects export (export renders cards with animate=false).
  const [play, setPlay] = useState(
    () => typeof sessionStorage !== 'undefined' && !sessionStorage.getItem('mp_landed') && !reducedMotion(),
  )
  const playRef = useRef(play)
  // MP-OS-196: the monthly PDF captures its own off-screen chart, never the Sales Trends one.
  const reportChartRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let cancelled = false
    setDos(null)
    setDosError(false)
    fetchDosLayer(period.value)
      .then((d) => !cancelled && setDos(d))
      .catch(() => {
        if (!cancelled) {
          setDos(null)
          setDosError(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [periodKey])

  useEffect(() => {
    if (!playRef.current) return
    sessionStorage.setItem('mp_landed', '1')
    const t = window.setTimeout(() => setPlay(false), 1300)
    return () => window.clearTimeout(t)
  }, [])

  useEffect(() => {
    if (assortment.data && !assortment.segments.includes(requestedSegment)) {
      setRequestedSegment('ALL')
      setFilterAttempt(n => n + 1)
    }
  }, [assortment.data, requestedSegment])

  // A5/A8 — ONE brand selection drives BOTH brand-level charts, and it is free over the
  // whole market rather than four hardcoded chips. "Per märke" splits the total into one
  // series per selected brand; off, both charts show the aggregate. This replaced five
  // states (compare/cmpBrands/byBrand/shareCompare/shareByBrand) whose combinations
  // meant the same two things.
  const [brandOptions, setBrandOptions] = useRequestState<BrandOption[]>(requestedSegment, [])
  // The static tenant registry only covers onboarded suppliers (brandHighlight/ownBrands in
  // tenant.ts). A manufacturer the admin picks that isn't in that registry (e.g. Björkkrans/
  // CAMO) has no static own brand at all, so TENANT.ownBrands is empty for it — yet Heart
  // still reports which brand is its own (BrandOption.own). Without folding that in here,
  // the own brand loses its reference position (orderedSelection) and its emphasis styling
  // (chart colour/weight) the moment any other brand is plotted alongside it — the exact
  // "own brand disappears" symptom reported for comparisons like ZYN + VELO.
  // MP-OS-188 bug #1 — every onboarded supplier already carries brandHighlight inside
  // ownBrands (tenant.ts), but an unregistered manufacturer resolved only from the server
  // (BrandOption.own) would otherwise lose it the moment it isn't echoed back as an own
  // BrandOption, so it is folded in explicitly rather than assumed redundant.
  const resolvedOwnBrands = useMemo(() => [...new Set([
    ...TENANT.ownBrands, ...(TENANT.brandHighlight ? [TENANT.brandHighlight] : []),
    ...brandOptions.filter((o) => o.own).map((o) => o.brand),
  ])], [brandOptions])
  const [brandSel, setBrandSel] = useState<string[]>(() => persisted?.brandSel ?? defaultSelection(TENANT.ownBrands))
  const [perBrand, setPerBrand] = useState(() => persisted?.perBrand ?? false)
  // MP-OS-188 bug #5 — keep the persisted copy identical to the live UI state (and therefore
  // to the actual API parameters built from it) on every change, not just on Reset, so a
  // navigate-away-and-back always restores exactly what was active, including mid-edit.
  useEffect(() => {
    saveTrendFilters(storageKey, { segment, requestedSegment, strength, fmt, category, flavour, nicotine, brandSel, perBrand,
      view: { freq, win, showCustom, showPartial, indexed, acc, avg, cmpPrev, rollOverride, showAdv } })
  }, [storageKey, segment, requestedSegment, strength, fmt, category, flavour, nicotine, brandSel, perBrand,
    freq, win, showCustom, showPartial, indexed, acc, avg, cmpPrev, rollOverride, showAdv])
  // Brand and product filters back to All. The full Reset also returns the view controls; the
  // Reset next to a selected value without sales (MP-OS-201) keeps the window the user chose.
  const resetFilters = () => {
    setFiltersLoading(true); changedFilter.current = undefined; setBrandSel([]); setPerBrand(false)
    setStrength('All'); setFmt('All'); setCategory('All'); setFlavour('All'); setNicotine('All'); setFilterAttempt(n => n + 1)
  }
  const needsFacetValidation = perBrand || [strength, fmt, category, flavour, nicotine].some(value => value !== 'All')
  // Stored-sales charts with no selected attributes do not depend on live facets.
  // Active selections remain blocked until validated; never drop a user's filter.
  // A segment restored from an earlier visit is confirmed against the current assortment
  // before any request uses it (MP-OS-188); ALL is always valid.
  const segmentUnconfirmed = requestedSegment !== 'ALL' && (!assortment.settled
    || (!!assortment.data && !assortment.segments.includes(requestedSegment)))
  // The loading/slow line in the status slot is shown only while filters load without error
  // (MPD-62: its visibility and its StatusText's `active` use this one flag).
  // MPD-133 L10: an active selection blocks the charts only until the filter options have validated
  // it once. A refetch for the same selection (a window or Group by change) runs beside the charts,
  // which draw with the current selection; a transient error then keeps the last options.
  const selectionKey = JSON.stringify([requestedSegment, strength, fmt, category, flavour, nicotine, perBrand, brandSel])
  const [validatedSelection, setValidatedSelection] = useState<string | null>(null)
  const selectionValidated = validatedSelection === selectionKey
  const filtersLineShown = filtersLoading && !filtersError
  const chartsBlocked = segmentUnconfirmed || segment !== requestedSegment
    || (needsFacetValidation && !selectionValidated && (filtersLoading || filtersError))
  // A requested segment without active attributes needs no facet validation, so its
  // charts are committed now rather than after the filter options arrive (for example
  // when an unavailable persisted segment falls back to All).
  useEffect(() => {
    if (!needsFacetValidation && segment !== requestedSegment) setSegment(requestedSegment)
  }, [needsFacetValidation, segment, requestedSegment])
  useEffect(() => {
    let cancelled = false
    const controller = new AbortController()
    if (segmentUnconfirmed) return
    setFiltersLoading(true)
    setFiltersError(false)
    setFiltersSlow(false)
    const values = (v: string) => v === 'All' ? [] : [v]
    Promise.all([fetchBrands(segmentApiValue(requestedSegment)), fetchProductFilters(segmentApiValue(requestedSegment), {
      brand: perBrand ? brandSel : [], strength: values(strength), format: values(fmt),
      category: values(category), flavour: values(flavour), nicotine: values(nicotine),
    }, changedFilter.current, controller.signal, () => { if (!cancelled) setFiltersSlow(true) },
    // MP-OS-201: values are offered only with sales in the trend window, so they follow it.
    { from: range.from, to: range.to })])
      .then(([brands, result]) => {
        if (cancelled) return
        const sel = (key: ProductFacet) => result.selected[key]?.[0] ?? 'All'
        setSelectionSales({ key: salesScopeKey({ manufacturer: TENANT.manufacturer, segment: requestedSegment,
          from: range.from, to: range.to, strength: sel('strength'), fmt: sel('format'), category: sel('category'),
          flavour: sel('flavour'), nicotine: sel('nicotine'), brands: perBrand ? result.selected.brand ?? [] : [] }),
        sold: result.matching_sold_cu })
        const validBrands = new Set((result.options.brand ?? []).map(b => b.value.toLowerCase()))
        setBrandOptions(brands.filter(b => validBrands.has(b.brand.toLowerCase())))
        setFacets(result)
        setAttrs({ strength: result.options.strength ?? [], format: result.options.format ?? [] })
        setStrength(result.selected.strength[0] ?? 'All')
        setFmt(result.selected.format[0] ?? 'All')
        setCategory(result.selected.category[0] ?? 'All')
        setFlavour(result.selected.flavour[0] ?? 'All')
        setNicotine(result.selected.nicotine[0] ?? 'All')
        setSegment(requestedSegment)
        setBrandSel(result.selected.brand)
        setValidatedSelection(JSON.stringify([requestedSegment, sel('strength'), sel('format'), sel('category'), sel('flavour'),
          sel('nicotine'), perBrand, result.selected.brand]))
        setFiltersLoading(false)
        setFiltersSlow(false)
      }).catch(() => { if (!cancelled) { setFiltersError(true); setFiltersLoading(false); setFiltersSlow(false) } })
    return () => { cancelled = true; controller.abort() }
  }, [requestedSegment, segmentUnconfirmed, filterAttempt, strength, fmt, category, flavour, nicotine, perBrand, brandSel.join('|'),
    range.from, range.to, TENANT.manufacturer])
  useEffect(() => {
    if (!brandSel.length) { setPerBrand(false); setIndexed(false) }
  }, [brandSel])
  const plotBrands = orderedSelection(brandSel, resolvedOwnBrands)
  const plotKey = plotBrands.join('|') // stable dep: the identity of the selection
  // MP-OS-188 round 3, bug #1 — a supplier comparing competitors only keeps its own total on
  // screen as a reference series (trendSeries.ts); both charts and both titles follow this plan.
  const seriesPlan = trendSeriesPlan({ manufacturer: TENANT.manufacturer, scopeLabel: SCOPE, perBrand, plotBrands, ownBrands: resolvedOwnBrands })
  const referenceSeries = seriesPlan.reference
  const chartOwnBrands = seriesPlan.emphasis

  // MPD-84 N2/S5: what a supplier's share curve is a share of. A segment is its permitted market
  // (narrowed for Vape/Smoking); with no segment, brand curves and filtered curves read against the
  // permitted segments. Only the supplier's own, unfiltered curve is the whole market (the one
  // exception, docs/supplier-market-scope.md). Admin whole market: null.
  const shareMarket = !TENANT.manufacturer ? null
    : segArg ? segmentMarketScope(segArg, SCOPE)
      : perBrand || shareFilters.length > 0 ? segmentMarketScope('permitted', SCOPE) : null

  const brandKey = `${trendKey}|${plotKey}|${perBrand}|${referenceSeries ?? ''}`
  const [brandChart, setBrandChart] = useRequestState<Chart | null>(brandKey, null)
  const [brandState, setBrandState] = useRequestState<'loading' | 'ready' | 'error'>(brandKey, 'loading')
  useEffect(() => {
    if (chartsBlocked) return

    if (!perBrand || plotBrands.length === 0) {
      setBrandChart(null)
      return
    }
    let cancelled = false
    setBrandChart(null)
    setBrandState('loading')
    const brands = plotBrands
    const reference = referenceSeries
    void Promise.all([
      // The supplier's own total, with exactly the filters the brand series use.
      reference
        ? fetchDosTrend({ manufacturer: TENANT.manufacturer, from: range.from, to: range.to, freq, segment: segArg, strength: strengthArg, fmt: fmtArg, ...extraFilters })
        : Promise.resolve(null),
      ...brands.map((b) =>
        fetchDosTrend({ brand: b, from: range.from, to: range.to, freq, segment: segArg, strength: strengthArg, fmt: fmtArg, ...extraFilters }),
      ),
    ]).then(([referenceChart, ...charts]) => {
      if (cancelled) return
      setBrandChart(mergeTrends([
        ...(reference ? [{ name: reference, chart: referenceChart }] : []),
        ...brands.map((name, i) => ({ name, chart: charts[i] })),
      ]))
      setBrandState('ready')
    }).catch(() => !cancelled && setBrandState('error'))
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perBrand, plotKey, referenceSeries, range.from, range.to, freq, segment, strength, fmt, category, flavour, nicotine, trendAttempt, chartsBlocked])

  // Signaler rail — proactive, query-backed findings the system surfaces without
  // being asked (the "push" surface, folded onto Översikt). null = still computing.
  const insightKey = `${periodKey}|${history.from}|${history.to}|${view}`
  const [insights, setInsights] = useRequestState<Insight[] | null>(insightKey, null)
  useEffect(() => {
    if (view !== 'overview') return
    let cancelled = false
    setInsights(null)
    void computeInsights(period, history)
      .then((r) => !cancelled && setInsights(r))
      .catch(() => !cancelled && setInsights([]))
    return () => {
      cancelled = true
    }
  }, [insightKey])

  // Marknadsandel över tid (MP-OS-73) — the andel-curve, sharing the sold curve's window
  // + granularity. The segment toggle picks the denominator (All White is Olof's default;
  // "Total" = whole market). Replaces the old static "Andel av All White" bar graph.
  const [shareChart, setShareChart] = useRequestState<Chart | null>(trendKey, null)
  const [shareError, setShareError] = useRequestState(trendKey, false)
  const [shareLoading, setShareLoading] = useRequestState(trendKey, true)
  useEffect(() => {
    if (chartsBlocked) return

    if (WHOLE_MARKET) {
      setShareChart(null)
      setShareError(false)
      return
    }
    let cancelled = false
    setShareError(false)
    setShareLoading(true)
    setShareChart(null)
    void fetchMarketShareTrend({
      manufacturer: TENANT.manufacturer,
      from: range.from,
      to: range.to,
      freq,
      segment: segArg,
      strength: strengthArg,
      fmt: fmtArg, ...extraFilters,
    })
      .then((c) => {
        if (cancelled) return
        setShareChart(c)
        setShareLoading(false)
      })
      .catch(() => { if (!cancelled) { setShareError(true); setShareLoading(false) } })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, freq, segment, strength, fmt, category, flavour, nicotine, trendAttempt, chartsBlocked])

  // The share counterpart to the per-brand split — the SAME selection, so picking Velo
  // once shows Velo in both charts. Olof: "om jag väljer att titta på XQS, då vill jag ju
  // se andelen på XQS."
  const [shareBrandChart, setShareBrandChart] = useRequestState<Chart | null>(brandKey, null)
  const [shareBrandState, setShareBrandState] = useRequestState<'loading' | 'ready' | 'error'>(brandKey, 'loading')
  useEffect(() => {
    if (chartsBlocked) return

    if (!perBrand || plotBrands.length === 0) {
      setShareBrandChart(null)
      return
    }
    let cancelled = false
    setShareBrandChart(null)
    setShareBrandState('loading')
    const brands = plotBrands
    const reference = referenceSeries
    void Promise.all([
      // MPD-84 N2: the own reference curve reads against the same permitted market as the brands.
      reference
        ? fetchMarketShareTrend({ manufacturer: TENANT.manufacturer, from: range.from, to: range.to, freq, segment: segArg, strength: strengthArg, fmt: fmtArg, ...extraFilters, permitted_market: !!TENANT.manufacturer })
        : Promise.resolve(null),
      // MPD-81 S3: the supplier scope travels with each brand curve too (preview = supplier).
      ...brands.map((b) =>
        fetchMarketShareTrend({ brand: b, ...(TENANT.manufacturer ? { manufacturer: TENANT.manufacturer } : {}), from: range.from, to: range.to, freq, segment: segArg, strength: strengthArg, fmt: fmtArg, ...extraFilters }),
      ),
    ]).then(([referenceChart, ...charts]) => {
      if (cancelled) return
      setShareBrandChart(mergeTrends([
        ...(reference ? [{ name: reference, chart: referenceChart }] : []),
        ...brands.map((name, i) => ({ name, chart: charts[i] })),
      ]))
      setShareBrandState('ready')
    }).catch(() => !cancelled && setShareBrandState('error'))
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perBrand, plotKey, referenceSeries, range.from, range.to, freq, segment, strength, fmt, category, flavour, nicotine, trendAttempt, chartsBlocked])

  // #7 Period-delta: this period's förpackningar vs the previous comparable
  // period (förp, cube). prevPeriod is null for multi-month windows → the
  // scorecard says "jämförbar period saknas" instead of dividing by 2025 noise.
  const prevPeriod = prevPeriodOf(period)
  const [prevForp, setPrevForp] = useRequestState<number | null>(periodKey, null)
  useEffect(() => {
    if (!prevPeriod) {
      setPrevForp(null)
      return
    }
    let cancelled = false
    setPrevForp(null)
    void fetchDosTrend({ manufacturer: TENANT.manufacturer, from: prevPeriod.from, to: prevPeriod.to, freq: 'month' })
      .then((c) => !cancelled && setPrevForp(sumSeries(c)))
      .catch(() => {})
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, trendAttempt])

  // #50 Ghost series: the previous period's förp curve (at the current freq) for the
  // accumulated overlay. Fail-safe — null when there's no comparable period.
  const previousKey = `${trendKey}|${prevRange.from}|${prevRange.to}|${cmpPrev}`
  const [prevChart, setPrevChart] = useRequestState<Chart | null>(previousKey, null)
  const [prevState, setPrevState] = useRequestState<'loading' | 'ready' | 'error'>(previousKey, 'ready')
  useEffect(() => {
    if (chartsBlocked) return

    // Only the "Ackumulerat" overlay uses the ghost curve — fetch it when that's on,
    // not on every mount (keeps it out of the first-paint query burst). The ghost is
    // the window of equal length immediately before the current one (windowPrevRange).
    if (!cmpPrev) {
      setPrevChart(null)
      return
    }
    let cancelled = false
    setPrevChart(null)
    setPrevState('loading')
    void fetchDosTrend({
      manufacturer: TENANT.manufacturer,
      from: prevRange.from,
      to: prevRange.to,
      freq,
      segment: segArg,
      strength: strengthArg,
      fmt: fmtArg, ...extraFilters,
    })
      .then((c) => { if (!cancelled) { setPrevChart(c); setPrevState('ready') } })
      .catch(() => !cancelled && setPrevState('error'))
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmpPrev, prevRange.from, prevRange.to, freq, segment, strength, fmt, category, flavour, nicotine, trendAttempt, chartsBlocked])

  // CU trends read the local dos-layer independently of other metrics.
  useEffect(() => {
    if (chartsBlocked) return

    let cancelled = false
    setLoading(true)
    setHeroError(false)
    setHero(null)
    void fetchDosTrend({
      manufacturer: TENANT.manufacturer,
      from: range.from,
      to: range.to,
      freq,
      segment: segArg,
      strength: strengthArg,
      fmt: fmtArg, ...extraFilters,
    })
      .then((c) => {
        if (cancelled) return
        setHero(c)
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setHeroError(true)
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, freq, segment, strength, fmt, category, flavour, nicotine, trendAttempt, chartsBlocked])

  // MP-OS-196: the monthly report's own trend, from report state only (month + supplier).
  const reportData = monthlyReportData(period, { manufacturer: TENANT.manufacturer, label: SCOPE }, history.from)
  const reportTrendKey = JSON.stringify([periodKey, reportData.request, trendAttempt])
  const [reportTrend, setReportTrend] = useRequestState<Chart | null>(reportTrendKey, null)
  const [reportTrendSettled, setReportTrendSettled] = useRequestState(reportTrendKey, false)
  useEffect(() => {
    if (view !== 'overview') return
    let cancelled = false
    setReportTrend(null)
    setReportTrendSettled(false)
    void fetchMonthlyReportTrend(reportData.request)
      .then((c) => { if (!cancelled) { setReportTrend(c?.data.length ? c : null); setReportTrendSettled(true) } })
      .catch(() => { if (!cancelled) { setReportTrend(null); setReportTrendSettled(true) } })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportTrendKey, view])

  // Validation badge with the HONEST delta vs Olof's monthly report (MPD-190: within ±5 % matches).
  const facit = dos ? facitBadge(dos) : null
  // MPD-64: on screen a short status with the detail in an ⓘ; the PDF prints both (facit.report).
  const facitShort = (label: string) => facit ? (
    <>
      {facit.label}
      <InfoTip label={label} size="small">{facit.tip}</InfoTip>
    </>
  ) : null
  // Headline KPI total + period delta are for the SELECTED month — take it from the dos-layer
  // snapshot (always the selected month), NOT sum(hero): the hero curve now spans the chosen
  // trend window (windowRange), which is decoupled from the month, so summing it would inflate
  // the delta (the +302 %-vs-April regression the walkthrough caught). Fall back to the series
  // before the snapshot loads.
  const curForp = dos != null ? dos.own.dosor : sumSeries(hero)
  // Gate the delta on the selected-month snapshot (dos) + a non-zero previous period. Computing
  // it before dos loads would risk a bogus value; no snapshot → show the period figure, never a
  // fabricated delta.
  const delta =
    dos != null && prevForp != null && prevForp > 0 && !period.ongoing && !period.settling
      ? { abs: curForp - prevForp, pct: ((curForp - prevForp) / prevForp) * 100 }
      : null
  // On accumulated, overlay the preceding equal-length window as a ghost line (MP-OS-50).
  const titles = trendChartTitles(seriesPlan, {
    perBrand, wholeMarket: WHOLE_MARKET, scopeLabel: SCOPE,
    segmentSuffix: segment !== 'ALL' ? ` · ${segmentLabel(segment)}` : '',
  })
  const prevLabel = prevRangeMode(win, freq) === 'year' ? 'Same period last year' : 'Previous period'
  const ghostChart =
    cmpPrev && !perBrand && prevChart && hero
      ? withGhost(hero, prevChart, 'Selected period', prevLabel)
      : null
  // The overlays apply to whichever series set is on screen — aggregate, per-brand or
  // the previous-period comparison.
  const base = perBrand ? brandChart : ghostChart ?? hero
  const transformed = base ? transformSeries(base, { acc, avg, window: rollWindow }) : null
  // Indexing is the last step: it rebases whatever curve the overlays produced.
  const shown = visibleTrend(transformed && indexed && perBrand ? indexSeries(transformed) : transformed, showPartial)
  // MP-OS-198: where the strength/format filter misses much of a period's CU (unknown values), one
  // visible warning above both charts; the per-period coverage goes in its ⓘ and the trend CSV.
  // MPD-133 M6: from the series actually shown: the per-brand charts in per-brand mode (one source
  // per brand curve, so the warning names the curve), else the total CU and share curves.
  const coverageCu = perBrand ? brandChart : hero
  const coverageShare = WHOLE_MARKET ? null : perBrand ? shareBrandChart : shareChart
  const coverageHidden = new Set(showPartial ? [] : [coverageCu, coverageShare].flatMap((c) =>
    (c?.data ?? []).filter((d) => d.period_kind === 'partial').map((d) => String(d.name))))
  const coverageNotes = coverageWarnings([
    ...chartCoverageSources('Sales (CU)', coverageCu),
    ...chartCoverageSources('Share market', coverageShare),
  ], freq, coverageHidden)

  // MP-OS-136 — annotate a point by clicking it. Olof's reason for wanting this: an owner
  // change or a campaign shows up as a step in the curve, and by next year nobody remembers
  // which week it happened. The note is stored against the DATES the clicked bucket covers,
  // so it stays attached to the right moment when the granularity changes.
  const [notes, setNotes] = useState<Annotation[]>([])
  const [noteAt, setNoteAt] = useState<{ from: string; to: string; nonce: number } | null>(null)
  const notesRef = useRef<HTMLDivElement>(null)
  // MPD-132: chart sections and anchors scroll to just below the sticky Group by/Segment bar.
  const stickyRef = useRef<HTMLDivElement>(null)
  useStickyOffset(stickyRef, view === 'trends')
  const noteBuckets = noteBucketMap((shown?.data ?? []).map((d) => String(d.name)), freq, notes)
  const annotatePoint = (bucket: string) => {
    const r = bucketRange(bucket, freq)
    if (!r) return // an unrecognised label would otherwise file the note under a guessed date
    setNoteAt({ ...r, nonce: Date.now() })
    // The notes strip sits under both charts, so on a short viewport the form it opens can be
    // off-screen — the click would look like it did nothing.
    notesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }
  // S5 — the latest actual data point, printed by the trend so Olof reads the freshest figure
  // without hovering the curve ("vi har ju skrivit ut senaste punkten hos oss"). Only for the
  // single total series (per-brand/indexed curves have no single meaningful "latest").
  const latestPoint =
    !perBrand && !indexed && shown?.data?.length ? shown.data[shown.data.length - 1] : null
  // A supplier with a real month KPI (from the cube) but an empty dos-layer curve isn't
  // "no sales" — its dos-trend just isn't materialized in the nightly sweep yet. Say that
  // honestly instead of "Ingen data för perioden" (which reads as zero). Dos-layer charts only.
  const trendEmptyMsg =
    dos != null && dos.own.dosor > 0
      ? 'No finished trend curve for this selection yet. Monthly totals are available in Overview.'
      : 'No data for this period.'
  // MP-OS-201: when the selection sold nothing in the window, both charts say so in the same
  // words. Only for the scope the answer was computed for, and not for an unfiltered supplier
  // whose curve is merely not materialized yet (the message above stays for that case).
  const noSales = !filtersLoading && !filtersError && selectionSales?.sold === 0
    && selectionSales.key === salesScopeKey({ manufacturer: TENANT.manufacturer, segment, from: range.from, to: range.to,
      strength, fmt, category, flavour, nicotine, brands: perBrand ? brandSel : [] })
    && !(dos != null && dos.own.dosor > 0 && !needsFacetValidation)
  // An empty select says why: no catalogue values, or none with sales in the window.
  const emptyFacetText = (key: ProductFacet, catalogueText: string) =>
    facets?.matching_sold_cu !== undefined && !facets.unavailable.includes(key)
      ? `No ${{ brand: 'brands', category: 'categories', strength: 'strengths', format: 'formats', flavour: 'flavours', nicotine: 'nicotine levels' }[key]} with sales in this period`
      : catalogueText
  // A selected value without sales is kept and marked; Reset sits right next to it.
  const noSalesReset = (options: readonly AttributeOption[], value: string) => {
    const empty = selectedWithoutSales(options, value)
    return empty ? (
      <button type="button" onClick={resetFilters}
        aria-label={`Reset filters (${empty.label ?? empty.value}${NO_SALES_MARK})`}>Reset filters</button>
    ) : null
  }
  // MPD-68: a supplier's share card and monthly report follow its MAIN segment (the API's
  // own_main_segment); the whole market keeps All White in the report, as before.
  const mainSeg = dos && TENANT.manufacturer ? mainSegmentOf(dos) : null
  const aw = (mainSeg ? mainSeg.brands : dos?.all_white_brands_dosor ?? []).slice(0, 6)
  const reportSegment = mainSeg ? (mainSeg.main ? mainSegmentLabel(mainSeg.main.segment) : null) : 'All White'
  // The month the snapshot is for, so a stale figure cannot claim the newly selected one.
  const dosMonthLabel = dos ? periods.find((p) => p.value === dos.month)?.label ?? period.label : period.label
  const lbo = dos?.largest_brand_overall ?? null // #116 — largest brand overall (all segments)
  // The snapshot carries the month it is for, so a figure left standing from the previous
  // selection identifies itself — no extra state, and it cannot drift out of sync.
  const dosStale = dos != null && dos.month !== period.value
  // V1 reports use the selected CU snapshot. Customer semantics await MP-OS-171.
  // MP-OS-196: and once its own trend has loaded (or failed: the PDF then has no trend image).
  const reportReady = dos != null && !dosStale && reportTrendSettled
  // MPD-133 L6: the report trend failed, 404'd or came back empty: say so next to the button and in the PDF.
  const reportTrendUnavailable = reportTrendSettled && !reportTrend
  // MP-OS-192: tell the page what the share card shows (segment + month, or nothing), so the
  // segment rows below hide only a % the card already states. Derived from the card's own state —
  // the dos-layer snapshot and the period toggle — not fetched a second time.
  const shareFigure = view === 'overview' && dos && mainSeg
    ? shareCardFigure({ main: mainSeg.main, month: dos.month, allTime: shareAllTime })
    : null
  const shareFigureSegment = shareFigure?.segment
  const shareFigureMonth = shareFigure?.month
  useEffect(() => {
    if (!onShareCard) return
    onShareCard(shareFigureSegment && shareFigureMonth ? { segment: shareFigureSegment, month: shareFigureMonth } : null)
    return () => onShareCard(null)
  }, [onShareCard, shareFigureSegment, shareFigureMonth])

  // MP-OS-190: every download on this page goes through the shared guard (useDownload) and is
  // named by buildExportFilename: English report type, supplier (or whole market), segment, period.
  const reportDownload = useDownload(async (signal) => {
    if (!dos || !reportReady) return SKIPPED
    // MP-OS-209: the PDF names a top article without its SKU, so a verified pack variant, else the
    // SKU for a name another top article shares, names it apart.
    const topSkus = dos.own_top_skus_dosor ?? []
    const sharedNames = ambiguousNames(topSkus.map((s) => ({ sku: s.sku, name: s.namn_sv, packVariant: s.pack_variant })))
    const report: MonthlyReport = {
      manufacturer: SCOPE,
      periodLabel: period.label,
      previousPeriodLabel: prevPeriod?.label ?? null,
      cuDeltaAbs: delta?.abs ?? null,
      cuDeltaPct: delta?.pct ?? null,
      dosor: dos.own.dosor,
      shareLabel: `${SCOPE} · share of ${reportSegment ?? 'its segments'}`,
      sharePct: mainSeg?.main?.share_pct ?? null,
      segment: reportSegment,
      marketSegment: mainSeg ? mainSeg.main?.market_dosor ?? null : dos.market_segment_dosor?.['All White'] ?? null,
      // MPD-73: the market row and heading use the card's scope wording (Vape/Smoking: own product types).
      marketScope: mainSeg ? (mainSeg.main ? segmentMarketScope(mainSeg.main.segment, SCOPE) : null) : segmentMarketScope('All White', SCOPE),
      reconciliation: facit?.report ?? 'Not reconciled against the monthly report for this period.',
      reconciliationStatus: facit?.status ?? 'none',
      reconciliationMark: facit?.pdfMark,
      trendCaption: reportData.caption,
      trendNote: reportTrendUnavailable ? REPORT_TREND_UNAVAILABLE_PDF : null,
      // The bucket without a brand in Heart keeps its row (its CU is in the total) under its
      // English label, and is never "your brand". "Your brand" is the server's own tag (MPD-73),
      // so it holds for any supplier and trend filter; only an older API falls back to the list.
      shareTable: aw.map((b) => b.resolved === false
        ? { brand: UNRESOLVED_BRAND_LABEL, pct: b.share_of_segment, own: false }
        : { brand: b.brand, pct: b.share_of_segment, own: TENANT.manufacturer ? b.own ?? isOwn(b.brand, resolvedOwnBrands) : false }),
      topSkus: topSkus.map((s) => ({
        label: articleName(s.sku, s.namn_sv, { kit: s.kit, kitContents: s.kit_contents, packVariant: s.pack_variant, ambiguous: sharedNames }),
        dosor: s.dosor,
        unnamed: isUnnamed(s.sku, s.namn_sv),
      })),
    }
    await exportMonthlyReport(report, reportTrend ? reportChartRef.current : null, buildExportFilename({
      reportType: REPORT_TYPES.monthlyReport,
      scope: EXPORT_SCOPE,
      segment: reportSegment,
      from: period.value, // the report's month, 'YYYY-MM'
      extension: 'pdf',
    }), signal)
  }, { timeoutMs: REPORT_TIMEOUT_MS, doneMs: REPORT_SUCCESS_MS })

  // MP-OS-210: both CSVs follow the selection on screen, say so in a Selection block and carry
  // the segment and a filter slug in their file names.
  const exportSegment = segment !== 'ALL' ? segmentLabel(segment) : null
  const activeFilters = exportFilters({ brands: perBrand ? brandSel : [], strength, fmt, category, flavour, nicotine })
  const exportSelection = (unit: string) => ({
    supplier: TENANT.manufacturer ? ACTIVE_SUPPLIER.label : null,
    from: range.from, to: range.to, segment: exportSegment, filters: activeFilters, unit,
  })
  const csvDownload = useDownload(() => {
    if (!shown) return SKIPPED
    exportTrendCsv(shown, buildExportFilename({
      reportType: REPORT_TYPES.salesTrend,
      scope: EXPORT_SCOPE,
      segment: exportSegment,
      filters: activeFilters,
      from: range.from,
      to: range.to,
      extension: 'csv',
    }), { ...exportSelection(shown.unit), frequency: FREQ_LABEL[freq], partialShown: showPartial,
      coverage: coverageSelectionRows(coverageNotes) })
  })

  const articlesDownload = useDownload((signal) => downloadArticlesExport(range.from, range.to, buildExportFilename({
    reportType: REPORT_TYPES.articles,
    scope: EXPORT_SCOPE,
    segment: exportSegment,
    filters: activeFilters,
    from: range.from,
    to: range.to,
    extension: 'csv',
  }), signal, {
    segment: segArg, brand: perBrand ? brandSel : [], strength: strengthArg, fmt: fmtArg, ...extraFilters,
  }))

  return (
    // MP-OS-191 D: on the Overview the segment cards follow directly, so the hero ends with the
    // same 16 px step its own cards use (App drops the section padding between the two).
    <section style={view === 'overview' ? { ...S.wrap, marginBottom: 'var(--sp-4)' } : S.wrap}>
      {view === 'overview' && <>
      {/* ONE horizontal band (Olof S5): the KPIs and the signal card(s) share one row so the
          top reads at a glance with less scrolling. MP-OS-192: the operational measures (CU sold,
          the All White share) come first; "Best day ever" is a highlight, so it sits last —
          right-most on a wide row, at the bottom when the band wraps to one column on a phone. */}
      <div className={`lh-kpis ${play ? 'lh-rise' : ''}`}>
        <Kpi
          rise={play}
          delay={0}
          stale={dosStale}
          label={`${SCOPE} · CU sold · ${period.label}`}
          value={dos ? <Num value={dos.own.dosor} play={play} fmt={formatNumber} /> : dosError ? '—' : '…'}
          delta={
            period.ongoing ? (
              <span style={S.kpiSnap}>ongoing · {throughLabel(period)}</span>
            ) : period.settling ? (
              // Calendar-complete but its orders are still maturing to 'completed'; the figure
              // is real-but-rising, so show it (value above) with a marker — never a whole-vs-whole
              // delta, which would read the not-yet-settled month as a false month-over-month dip.
              <span style={S.kpiSnap}>still settling · the figure is still rising</span>
            ) : delta ? (
              <>
                <Delta abs={delta.abs} pct={delta.pct} compact />
                <span style={S.kpiSnap}>vs {prevPeriod?.label}</span>
              </>
            ) : (
              <span style={S.kpiSnap}>
                {prevPeriod ? `as of ${period.label}` : <>no comparable period<InfoTip label="About the comparison" size="small">Data starts December 2025, so there is no earlier period to compare with.</InfoTip></>}
              </span>
            )
          }
          note={dosError ? 'could not load CU — try again in a moment' : facitShort('About the reconciliation') ?? undefined}
        />
        {!TENANT.manufacturer ? (
          // Whole-market profile: "andel av All White" would be a tautological 100 %, so show
          // the OVERALL market leader's share instead (#116 — incl. tobacco, not just All White).
          <Kpi
            rise={play}
            delay={140}
            stale={dosStale}
            label="Largest brand · overall"
            value={lbo ? <Num value={lbo.share_of_market} play={play} count={false} fmt={(n) => `${lbo.brand} · ${formatShare(n, { hasSales: true })}`} /> : dosError ? '—' : '…'}
            delta={<span style={S.kpiSnap}>as of {period.label}</span>}
            bar={lbo?.share_of_market ?? null}
            note={dosError ? 'could not load the share' : <>of whole market<InfoTip label="About the share" size="small">Share of all CU sold in the whole market, every segment including tobacco.</InfoTip></>}
          />
        ) : (
          (() => {
            // MP-OS-187 C / MPD-68: the supplier's OWN share of its MAIN segment (its largest by CU,
            // within its permitted segments). The label keeps the basis and period visible (MPD-64:
            // short, with the full definition and why this segment in the ⓘ); no sales is an empty
            // state, never a stated 0 %.
            const card = dos && mainSeg
              ? mainSegmentShareText({ main: mainSeg.main, scope: SCOPE, month: dosMonthLabel, allTime: shareAllTime })
              : null
            const shareVal = card?.value ?? null
            // MP-OS-192: the brand ranking is per month only, so the line names the month it is
            // for, and an own leader is never repeated as a figure — its competitor goes in the ⓘ.
            const lb = mainSeg ? largestBrand(mainSeg.brands, resolvedOwnBrands, mainSeg.competitor) : null
            const lbText = lb && card?.segment
              ? largestBrandText(lb, card.segment, dosMonthLabel, soldShare, card.marketNote)
              : null
            return (
              <Kpi
                rise={play}
                delay={140}
                stale={dosStale}
                label={<>
                  {card?.label ?? `Share of segment CU · ${shareAllTime ? 'all time' : period.label}`}
                  {/* Until the dos-layer response arrives (or when it fails) the segment is not
                      known, so the label and ⓘ stay generic rather than claim All White. */}
                  <InfoTip label="About the share" size="small">
                    {card?.labelTip ?? `${SCOPE}’s CU as a percentage of all CU sold in its largest segment, for the period chosen with the buttons (all time or the selected month).`}
                  </InfoTip>
                </>}
                value={shareVal != null
                  ? <Num value={shareVal} play={play} count={false} fmt={soldShare} />
                  : card?.empty ? <span style={S.kpiEmpty}>{card.empty}</span> : dosError ? '—' : '…'}
                delta={
                  /* A real segmented control (S.seg + chipOn), not two bare buttons — those
                     ran together as "läget i Juli 2026någonsin" in the a11y tree and read as
                     text rather than as a choice. */
                  <span style={S.seg} role="group" aria-label="Period for the share">
                    {(['month', 'all'] as const).map((m) => {
                      const on = (m === 'all') === shareAllTime
                      return (
                        <button
                          key={m}
                          aria-pressed={on}
                          style={{ ...chipOn(on), fontSize: 11.5, padding: '3px 9px' }}
                          onClick={() => setShareAllTime(m === 'all')}
                        >
                          {m === 'all' ? 'all time' : `as of ${period.label}`}
                        </button>
                      )
                    })}
                  </span>
                }
                bar={shareVal ?? null}
                note={
                  dosError
                    ? 'could not load the share'
                    : card?.chosenNote || lbText
                      ? <>
                          {card?.chosenNote && <div>{card.chosenNote}</div>}
                          {lbText && <div>{lbText.line}<InfoTip label="About the largest brand" size="small">{lbText.tip}</InfoTip></div>}
                        </>
                      : undefined
                }
              />
            )
          })()
        )}
        {(insights ?? []).map((ins) => {
          const pinned = pinnedInsightIds.has(ins.id)
          return (
            <div className="signal-card" key={ins.id}>
              <div className="signal-top">
                <span className="signal-title">{ins.title}</span>
                <button
                  className="signal-pin"
                  onClick={() => onPinInsight(ins)}
                  disabled={pinned}
                  title="Pin to dashboard"
                  aria-label="Pin to dashboard"
                >
                  <Pin size={12} />
                </button>
              </div>
              <div className="signal-value tnum">{ins.value}</div>
              <div className="signal-detail">{ins.detail}</div>
            </div>
          )
        })}
      </div>

      {/* B2 — the Scorecard keeps ONLY what is NOT already shown in the signal cards above:
          the vs-previous-period delta, the Monthly report action, and the reconciliation
          status. Its four metric rows were byte-for-byte the same figures (Olof: "det är
          exakt samma... så om vi kliner bort det"), and its month picker moved to the top of
          the page as the single global control (A4). */}
      <div style={{ ...S.card, ...(play ? { animationDelay: '230ms' } : {}) }} className={play ? 'lh-rise' : undefined}>
        <div style={S.head}>
          <span style={S.title}>{period.label}</span>
          <span style={S.tag}>{period.ongoing ? `ongoing · ${throughLabel(period)}` : prevPeriod ? `vs ${prevPeriod.label}` : 'comparison'}</span>
          <button style={S.chip} disabled={!reportReady || reportDownload.locked} onClick={() => reportDownload.start()}>
            {reportDownload.busy ? 'Generating report…' : reportDownload.done ? 'Report ready ✓' : 'Monthly report (PDF)'}
          </button>
        </div>
        {reportTrendUnavailable && <p role="status" className="trend-status-line" data-testid="report-trend-unavailable">{REPORT_TREND_UNAVAILABLE}</p>}
        {reportDownload.error && <DownloadError message="Could not create the monthly report. Please try again." onRetry={reportDownload.retry} />}
        {/* #116: the "vs previous month" row was byte-for-byte the delta already shown on the
            THE MARKET · CU SOLD card — Olof: "den där är bara att wipea". Removed; the scorecard
            keeps only the reconciliation status. */}
        <div style={{ ...S.scRow, borderTop: 0 }}>
          <span style={S.scLabel}>Reconciliation</span>
          <span style={S.scVal} data-testid="reconciliation-mark">{facit?.mark ?? '—'}</span>
          <span style={S.scNote}>
            {dosError
              ? 'could not load the reconciliation status'
              : facitShort('About the reconciliation status') ?? ''}
          </span>
        </div>
      </div>

      {/* MP-OS-196: the monthly PDF's own laid-out trend (report month + supplier only), off-screen. */}
      {reportTrend && <div aria-hidden="true" style={{ position: 'fixed', left: -10000, top: 0, width: 900, pointerEvents: 'none' }}>
        <div ref={reportChartRef} style={S.chartSlot}>
          <InsightChart chart={reportTrend} animate={false} />
        </div>
      </div>}
      </>}
      {view === 'trends' && <>
      <div style={{ ...S.head, justifyContent: 'flex-end', marginBottom: 10 }}>
        <span style={S.pickLabel}>
          {formatRangeLabel(range.from, range.to)}
          {/* MPD-64: was a green note box under this row. */}
          <InfoTip label="About these charts" size="small">
            Sales volume is shown in CU. Brand and product filters apply to the two main charts. Article
            comparison has its own article selection.
          </InfoTip>
        </span>
        <button
          style={S.chip}
          disabled={articlesDownload.locked}
          onClick={() => articlesDownload.start()}
          title="Export the market articles for the selected period, segment and filters as a CSV"
        >
          {articlesDownload.busy ? 'Exporting…' : articlesDownload.done ? 'Exported ✓' : 'Export all articles (CSV)'}
        </button>
      </div>
      {articlesDownload.error && <DownloadError message="Could not export articles. Please try again." onRetry={articlesDownload.retry} />}

      {/* MP-OS-188 bug #2 (round 2) — mounting/unmounting this <p> entirely used to collapse
          and re-expand the space above the sticky bar and the brand/strength row every time
          filtersLoading flipped — most visibly on a fresh load with a persisted (MP-OS-188
          bug #5) non-default segment, where this status line is briefly true before the very
          first facet fetch resolves. Always render it and toggle visibility instead of
          mounting, so its line box — and everything below it — never moves. visibility:hidden
          also keeps it correctly out of the accessibility tree (queryByRole('status') stays
          null) and out of screen-reader announcements while not loading. */}
      {/* Round 3 — the loading status, the slow-source notice and the filter error share ONE
          reserved single-line slot (App.css .trend-status-slot): the slot is always there and
          always one line tall, so none of them appearing, changing wording or disappearing can
          move the controls below. Long wording is truncated with an ellipsis (full text in the
          title); the Retry button stays visible outside the truncated text. MPD-60: whenever a
          message is cut, StatusText adds an info button that opens the full text in an overlay
          popover, so touch users can read it too without the slot growing. MPD-62: the loading
          line is only hidden, so its StatusText gets `active` from the same flag as its
          visibility and closes with the line. */}
      <div className="trend-status-slot">
        <p role="status" data-testid="trend-filters-status" className="trend-status-line" style={{ visibility: filtersLineShown ? 'visible' : 'hidden' }}>
          {filtersLoading && filtersSlow
            ? <><StatusText active={filtersLineShown} text={`Filters for ${segmentLabel(requestedSegment)} are taking longer than usual; still trying…`} /> <button onClick={() => setFilterAttempt(n => n + 1)}>Retry filters</button></>
            : <StatusText active={filtersLineShown} text={`Loading filters for ${segmentLabel(requestedSegment)}…`} />}
        </p>
        {filtersError && (selectionValidated
          ? <p role="status" className="trend-status-line" data-testid="trend-filters-stale"><StatusText text={`Could not refresh the filters for ${segmentLabel(requestedSegment)}; showing the last loaded options.`} /> <button onClick={() => setFilterAttempt(n => n + 1)}>Retry filters</button></p>
          : <p role="alert" className="trend-status-line"><StatusText text={`Could not load filters for ${segmentLabel(requestedSegment)}. Selected filters are unavailable until this request succeeds.`} /> <button onClick={() => setFilterAttempt(n => n + 1)}>Retry filters</button></p>)}
      </div>
      <div ref={stickyRef} className="sales-trend-sticky" role="group" aria-label="Shared trend controls">
            <span style={S.pickGroup}>
              <span style={S.pickLabel}>Group by</span>
              <span style={S.seg}>
                {(['day', 'week', 'month'] as Freq[]).map((f) => (
                  <button key={f} aria-pressed={f === freq} style={segBtn(f === freq)} onClick={() => changeFreq(f)}>{FREQ_LABEL[f]}</button>
                ))}
              </span>
            </span>
              {/* MP-OS-188 bug #2 — this bar is position:sticky on desktop (App.css
                  .sales-trend-sticky), so any height change here pushes every control below it,
                  including the brand control, right as the user may be clicking one. Round 3
                  makes the segment control's height independent of the number of segments AND
                  of the width: the pills sit in ONE non-wrapping row (ScrollRow) that scrolls
                  horizontally with an edge fade when it doesn't fit, and the group's flex basis
                  is fixed (App.css .trend-seg-group), so whether it shares a line with Group by
                  never depends on how many pills it holds. The disabled placeholders, the loaded
                  set (larger or smaller) and the error message all occupy that same single row. */}
              <span className="trend-seg-group" style={{ ...S.pickGroup, flexWrap: 'nowrap' }}>
                <span style={S.pickLabel}>Segment</span>
                <ScrollRow className="trend-seg-row">
                  {assortment.error && <span role="alert" className="trend-seg-error">Could not load segments. <button onClick={assortment.retry}>Try again</button></span>}
                  {!assortment.data && !assortment.error && (
                    <span className="trend-seg-pills" style={S.segRow} aria-busy="true" aria-label="Loading segments">
                      {SEGMENTS.map((s) => (
                        <button key={s} disabled aria-hidden="true" tabIndex={-1} style={{ ...segBtn(false), opacity: 0.45 }}>
                          {segmentLabel(s)}
                        </button>
                      ))}
                    </span>
                  )}
                  {assortment.data && <span className="trend-seg-pills" style={S.segRow}>
                    {assortment.segments.map((s) => (
                      <button key={s} aria-pressed={s === segment} data-active={s === segment || undefined} style={segBtn(s === segment)} onClick={() => {
                        setFiltersLoading(true); setFiltersError(false); setFiltersSlow(false); changedFilter.current = undefined
                        if (s !== requestedSegment) {
                          // A new assortment starts with its own unfiltered selection.
                          // Old attributes/brands must not appear under the new segment.
                          setStrength('All'); setFmt('All'); setCategory('All'); setFlavour('All'); setNicotine('All')
                          setBrandSel([]); setPerBrand(false); setIndexed(false); setSegment(s)
                        }
                        setRequestedSegment(s); setFilterAttempt(n => n + 1)
                      }}>
                        {segmentLabel(s)}
                      </button>
                    ))}
                  </span>}
                </ScrollRow>
              </span>
        <span className="sales-trend-period">
          {formatRangeLabel(range.from, range.to)}
          <InfoTip label="About the range" size="small">This range applies to the two main charts and to the article comparison.</InfoTip>
        </span>
      </div>
      <div role="group" aria-label="Sales trend filters" style={S.card}>
          {/* Row 1 — resolution (bucket size) + time span + export. Two distinct
              concepts, so each gets a label: "Visa per" = one point per dag/vecka/månad,
              "Tidsspann" = how far back the curve runs. */}
          <div style={{ ...S.head, marginBottom: 6 }}>
            <span style={S.pickGroup}>
              <span style={S.pickLabel}>Range</span>
              <span style={S.seg}>
                {presets.map((n) => {
                const active = win.kind === 'preset' && win.n === n
                return (
                  <button
                    key={n}
                    aria-pressed={active}
                    style={segBtn(active)}
                    onClick={() => { setWin({ kind: 'preset', n }); setShowCustom(false) }}
                    title={`Last ${n} ${WINDOW_UNIT[freq]}`}
                  >
                    {n} {WINDOW_UNIT[freq]}
                  </button>
                )
              })}
              <button
                aria-pressed={win.kind === 'ytd'}
                style={segBtn(win.kind === 'ytd')}
                onClick={() => { setWin({ kind: 'ytd' }); setShowCustom(false) }}
                title="Year to date — 1 Jan to the latest date with data"
              >
                Year to date
              </button>
              <button
                aria-pressed={win.kind === 'custom'}
                style={segBtn(win.kind === 'custom')}
                onClick={() => setShowCustom((v) => !v)}
                title="Pick your own date range"
              >
                Custom range
              </button>
              </span>
              <InfoTip label="About year to date" size="small">Year to date: 1 January to the latest date with data.</InfoTip>
            </span>
            <button
              style={{ ...S.chip, marginLeft: 'auto' }}
              disabled={!shown || chartsBlocked || csvDownload.locked}
              onClick={() => csvDownload.start()}
              title="Download the series (CU) as CSV — opens in Excel."
            >
              {csvDownload.busy ? 'Exporting…' : csvDownload.done ? 'Downloaded ✓' : 'Trend CSV'}
            </button>
          </div>
          {csvDownload.error && <DownloadError message="Could not export the trend. Please try again." onRetry={csvDownload.retry} />}
          {freq !== 'day' && <PartialPeriods checked={showPartial} onChange={setShowPartial} />}
          {/* Eget intervall — free from–to; editing either field switches to custom mode. */}
          {showCustom && (
            <div style={{ ...S.head, marginBottom: 6, gap: 8 }}>
              <input
                type="date"
                aria-label="From date"
                style={S.dateInput}
                value={range.from}
                min={minCustomFrom}
                max={range.to}
                aria-invalid={customRangeError ? true : undefined}
                aria-describedby={customRangeError ? 'trends-range-error' : undefined}
                onChange={(e) => {
                  const from = e.target.value
                  if (!from) return
                  if (from > range.to) {
                    setCustomRangeError('From date must be on or before To date. The previous valid range is kept.')
                    return
                  }
                  setWin({ kind: 'custom', from: clampMax(from, minCustomFrom), to: range.to })
                }}
              />
              <span style={S.dateArrow}>→</span>
              <input
                type="date"
                aria-label="To date"
                style={S.dateInput}
                value={range.to}
                min={range.from}
                max={dataEnd}
                aria-invalid={customRangeError ? true : undefined}
                aria-describedby={customRangeError ? 'trends-range-error' : undefined}
                onChange={(e) => {
                  if (!e.target.value) return
                  const to = e.target.value
                  if (to < range.from || to > dataEnd) {
                    setCustomRangeError(to < range.from
                      ? 'To date must be on or after From date. The previous valid range is kept.'
                      : 'To date cannot be later than the available data. The previous valid range is kept.')
                    return
                  }
                  const from = clampMax(range.from, isoMinusDays(to, MAX_CUSTOM_SPAN_DAYS))
                  setWin({ kind: 'custom', from, to })
                }}
              />
              {customRangeError && <p id="trends-range-error" role="alert" style={{ width: '100%', margin: 0 }}>{customRangeError}</p>}
            </div>
          )}
          {/* The three ordinary filters share a row. Selecting a brand switches to the
              per-brand curves; clearing the selection returns to the total. */}
          {(
            <div style={{ ...S.head, marginBottom: 6 }}>
              <fieldset disabled={filtersLoading || (filtersError && !selectionValidated) || !assortment.settled} aria-label="Brand, strength and format" style={{ ...S.head, width: '100%', margin: 0, padding: 0, border: 0 }}>
                <span style={S.pickGroup}>
                  <span style={S.pickLabel}>Brand</span>
                  <BrandPicker
                    compact
                    options={brandOptions}
                    selection={perBrand ? brandSel : []}
                    ownBrands={resolvedOwnBrands}
                    onChange={(next) => { setFiltersLoading(true); changedFilter.current = 'brand'; setBrandSel(next); setPerBrand(next.length > 0) }}
                  />
                </span>
                {facets?.fields.includes('strength') && (
                  <span style={S.pickGroup}>
                    <span style={S.pickLabel}>Strength</span>
                    <select
                      value={strength}
                      disabled={!attrs.strength.length}
                      onChange={(e) => { setFiltersLoading(true); changedFilter.current = 'strength'; setStrength(e.target.value) }}
                      aria-label="Filter by strength"
                      className="control-select" style={S.sel}
                    >
                      <option value="All">{attrs.strength.length ? 'All strengths' : emptyFacetText('strength', 'No catalog strength values')}</option>
                      {attrs.strength.map((o) => (
                        <option key={o.value} value={o.value}>{optionText(o, o.value)}</option>
                      ))}
                    </select>
                    {noSalesReset(attrs.strength, strength)}
                  </span>
                )}
                {facets?.fields.includes('format') && !isLooseSnus(category) && (
                  <span style={S.pickGroup}>
                    <span style={S.pickLabel}>Format</span>
                    <select
                      value={fmt}
                      disabled={!attrs.format.length}
                      onChange={(e) => { setFiltersLoading(true); changedFilter.current = 'format'; setFmt(e.target.value) }}
                      aria-label="Filter by format"
                      className="control-select" style={S.sel}
                    >
                      <option value="All">{attrs.format.length ? 'All formats' : emptyFacetText('format', 'No catalog format values')}</option>
                      {attrs.format.map((o) => (
                        <option key={o.value} value={o.value}>{optionText(o, o.value)}</option>
                      ))}
                    </select>
                    {noSalesReset(attrs.format, fmt)}
                  </span>
                )}
                {(['category', 'flavour', 'nicotine'] as const).filter(key => facets?.fields.includes(key)).map(key => {
                  const label = { category: 'Category', flavour: 'Flavour', nicotine: 'Nicotine content' }[key]
                  const current = { category, flavour, nicotine }[key]
                  const update = { category: setCategory, flavour: setFlavour, nicotine: setNicotine }[key]
                  const options = facets?.options[key] ?? []
                  const choices = [
                    { value: 'All', label: options.length ? { category: 'All categories', flavour: 'All flavours', nicotine: 'All nicotine levels' }[key] : key === 'nicotine' ? 'Awaiting verified mg/ml data' : emptyFacetText(key, 'No catalog values available') },
                    // MP-OS-191 #13: the option shows its English label; the value sent stays Heart's.
                    ...options.map(o => ({ value: o.value, label: optionText(o, `${o.label ?? (key === 'category' ? displayLabel(o.value) : o.value)}${key === 'nicotine' ? ' mg/ml' : ''}`) })),
                  ]
                  const pick = (value: string) => { setFiltersLoading(true); changedFilter.current = key; update(value) }
                  // MP-OS-189: flavours (dozens of catalog tags) and nicotine levels can run past ~10
                  // options and use the shared searchable picker; the 2–4 categories stay native.
                  // A <span>, not a <label>: a label forwards clicks on the picker's options to its button.
                  return <span key={key} style={S.pickGroup}><span style={S.pickLabel}>{label}</span>
                    {key === 'category'
                      ? <select aria-label={`Filter by ${key}`} className="control-select" style={S.sel} value={current} disabled={!options.length}
                          onChange={e => pick(e.target.value)}>
                          {choices.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      : <SearchableSelect label={`Filter by ${key}`} searchLabel={key === 'flavour' ? 'Search flavours' : 'Search nicotine levels'}
                          showAllLabel={key === 'flavour' ? 'Show all flavours' : 'Show all nicotine levels'}
                          style={S.sel} value={current} disabled={!options.length} onChange={pick} options={choices} />}
                    {noSalesReset(options, current)}
                  </span>
                })}
                {isLooseSnus(category) && <span>Format does not apply to {displayLabel(category).toLowerCase()}.</span>}
                <button onClick={() => { resetFilters(); resetView() }}>Reset filters</button>
              </fieldset>
            </div>
          )}
          <div style={{ ...S.head, marginBottom: 6, justifyContent: 'flex-end' }}>
            {perBrand && (
              <button aria-pressed={indexed} style={chipOn(indexed)} onClick={() => setIndexed((v) => !v)}
                title="Set each brand’s starting value to 100 to compare growth rather than level">
                Indexed
              </button>
            )}
            {perBrand && <InfoTip label="About indexed" size="small">Indexed sets each brand’s starting value to 100, so the chart compares growth rather than level.</InfoTip>}
            <button aria-pressed={showAdv} style={chipOn(showAdv)} onClick={() => setShowAdv((v) => !v)}>
              {showAdv ? 'Fewer options ▲' : 'More options ▾'}
            </button>
          </div>
          {showAdv && (
            <div style={{ ...S.head, marginBottom: 6 }}>
              <button aria-pressed={acc} style={chipOn(acc)}
                onClick={() => setAcc((v) => { if (!v) setAvg(false); return !v })}>Cumulative</button>
              <button aria-pressed={avg} style={chipOn(avg)}
                onClick={() => setAvg((v) => { if (!v) setAcc(false); return !v })}
                title={`Rolling average over ${rollWindow} ${FREQ_LABEL[freq].toLowerCase()}`}>Rolling average</button>
              {avg && (
                <span style={S.pickGroup}>
                  <span style={S.pickLabel}>Window</span>
                  <input type="number" min={2} max={26} value={rollWindow}
                    aria-label={`Number of ${FREQ_LABEL[freq].toLowerCase()} in the rolling average`}
                    style={{ ...S.dateInput, width: 62 }}
                    onChange={(e) => {
                      const n = Number(e.target.value)
                      setRollOverride(Number.isFinite(n) && n >= 2 ? Math.min(26, Math.round(n)) : null)
                    }} />
                  <span style={S.pickLabel}>{WINDOW_UNIT[freq]}</span>
                </span>
              )}
              {!perBrand && (
                <button aria-pressed={cmpPrev} style={chipOn(cmpPrev)} onClick={() => setCmpPrev((v) => !v)}
                  title={prevRangeMode(win, freq) === 'year'
                    ? 'Add the same period last year as a comparison curve'
                    : 'Add the preceding equal-length period as a comparison curve'}>
                  {prevRangeMode(win, freq) === 'year' ? 'Compare last year' : 'Compare previous'}
                </button>
              )}
            </div>
          )}
      </div>

      {!chartsBlocked && coverageNotes.map((w) => (
        <p key={w.attribute} role="status" className="coverage-warning" data-testid={`coverage-warning-${w.attribute}`}>
          {w.text}
          <InfoTip label={`About ${w.label.toLowerCase()} coverage`} size="small">
            Share of each period’s CU with a known {w.label.toLowerCase()} (below {COVERAGE_THRESHOLD} % is
            incomplete). The total CU is unchanged; only the {w.label.toLowerCase()} filter misses the rest.
            {w.details.map((d) => (
              <span key={d.source} style={{ display: 'block', marginTop: 6 }}>
                {d.source}: {d.periods.map((p) => `${p.label} ${coveragePercentText(p.pct)}`).join(' · ')}.
                {d.unbucketedCu > 0 && <> {unbucketedLine(w.attribute, formatNumber(d.unbucketedCu))}</>}
              </span>
            ))}
          </InfoTip>
        </p>
      ))}
      <div className="sales-trend-charts" hidden={chartsBlocked}>
        {/* HERO trend — DOSOR from the materialized dos-layer (daily, per the two-layer model) */}
        <div style={{ ...S.card, ...S.chartCard, ...(play ? { animationDelay: '290ms' } : {}) }} className={play ? 'lh-rise' : undefined}>
          <div style={S.head}>
            <span style={S.title} data-testid="trend-cu-title">{titles.cu}</span>
            {latestPoint && (
              <span style={{ marginLeft: 'auto', fontSize: 13, color: brand.greyText, whiteSpace: 'nowrap' }}>
                Latest:{' '}
                <span className="tnum" style={{ fontWeight: 700, color: brand.tealDark }}>
                  {formatNumber(Number(latestPoint.value))} CU
                </span>{' '}
                · {String(latestPoint.name)}
              </span>
            )}
            <span style={S.tag}>CU</span>
          </div>
          {/* Always show the resolved window + active overlays so the user can read
              back exactly what the curve covers, regardless of preset/custom/granularity. */}
          <div style={S.rangeCaption}>
            Showing {formatRangeLabel(range.from, range.to)}
            {segment !== 'ALL' ? ` · ${segmentLabel(segment)}` : ''}
            {strength !== 'All' ? ` · ${strength}` : ''}
            {fmt !== 'All' ? ` · ${fmt}` : ''}
            {extraCaption ? ` · ${extraCaption}` : ''}
            {perBrand ? ' · per brand' : ''}
            {perBrand && indexed ? ' · indexed (start = 100)' : ''}
            {acc ? ' · cumulative' : ''}
            {avg ? ` · rolling average (${rollWindow} ${WINDOW_UNIT[freq]})` : ''}
            {cmpPrev && !perBrand ? prevChart ? ` · vs ${prevLabel.toLowerCase()}`
              : prevState === 'loading' ? ' · loading comparison…'
                : prevState === 'error' ? ' · comparison could not be loaded' : ' · no comparable data available' : ''}
          </div>
          <div style={S.chartSlot}>
            {shown && !(noSales && !(perBrand ? brandState === 'loading' : loading) && !(perBrand ? brandState === 'error' : heroError)) ? (
              <InsightChart chart={shown} animate={play} ownBrands={chartOwnBrands} emptyMessage={trendEmptyMessage(base, showPartial, trendEmptyMsg)}
                            onPointClick={annotatePoint} noteBuckets={noteBuckets} />
            ) : (
              <div style={S.empty}>
              {(perBrand ? brandState === 'loading' : loading)
                ? 'Loading…'
                : (perBrand ? brandState === 'error' : heroError)
                  ? 'Could not load the curve — try again in a moment.'
                  : noSales ? NO_SALES_FOR_SELECTION : trendEmptyMsg}
              {(perBrand ? brandState === 'error' : heroError) && <div><button onClick={() => setTrendAttempt((n) => n + 1)}>Try again</button></div>}
            </div>
            )}
          </div>
        </div>

        {/* Marknadsandel över tid (MP-OS-73) — the andel-curve replacing the old static
            bar graph. Uses the shared trend filters and segment denominator. */}
        <div style={{ ...S.card, ...S.chartCard, ...(play ? { animationDelay: '360ms' } : {}) }} className={play ? 'lh-rise' : undefined}>
          <div style={S.head}>
            <span style={S.title} data-testid="trend-share-title">{titles.share}</span>
            <span style={{ ...S.tag, color: brand.tealDark }}>%</span>
          </div>
          <div style={S.rangeCaption}>
            {/* MP-OS-187: name the comparison market, the bucket and the range. MPD-64: that short
                basis stays visible; whose CU, the segment rule, filters and series go in the ⓘ. */}
            {`% of ${segment === 'ALL' ? (shareMarket ? 'CU in the permitted segments' : 'all CU in the whole market') : `${segmentDenominatorLabel(segment)} CU`} · per ${freq} · `}
            {formatRangeLabel(range.from, range.to)}
            <InfoTip label="About the share curve" size="small">
              {`${perBrand ? 'Each series’' : `${SCOPE}’s`} CU as a percentage of ${segment === 'ALL'
                ? (shareMarket ? `the CU in ${SCOPE}’s permitted segments` : 'all CU in the whole market (every category)')
                : `${shareMarket?.narrowed ? '' : 'all '}${segmentDenominatorLabel(segment)} CU in the market`}, per ${freq}.`}
              {/* MPD-76 F1: a supplier's segment denominator is its permitted market (the share card's wording). */}
              {shareMarket?.note ?? ''}
              {perBrand && TENANT.manufacturer ? ` Brands count only articles in ${SCOPE}’s segments.` : ''}
              {shareFilters.length > 0 ? ` Filters: ${shareFilters.join(' · ')}.` : ''}
              {perBrand ? ` Series: ${seriesPlan.series.join(' · ')}.` : ''}
            </InfoTip>
          </div>
          <div style={S.chartSlot}>
            {(() => {
              const shareShown = visibleTrend(perBrand ? shareBrandChart : shareChart, showPartial)
              // C11 — in the whole-market profile the aggregate share is the market's share of
              // itself: a flat 100 % line. Say so and point at the per-brand view, which IS a
              // real measure in this scope (each brand's share of the market over time).
              if (WHOLE_MARKET && !perBrand) {
                return (
                  <div style={S.empty}>
                    The market’s share of itself is always 100 %. Select a <strong>Brand</strong> above
                    to see how the brands divide the market over time — or pick a supplier.
                  </div>
                )
              }
              const shareBusy = perBrand ? shareBrandState === 'loading' || shareBrandState === 'error' : shareLoading || shareError
              return shareShown && !(noSales && !shareBusy) ? (
                <InsightChart chart={shareShown} animate={play} ownBrands={chartOwnBrands} emptyMessage={trendEmptyMessage(perBrand ? shareBrandChart : shareChart, showPartial, "No share data for this period.")}
                              onPointClick={annotatePoint} noteBuckets={noteBuckets} />
              ) : (
                <div style={S.empty}>
                  {(perBrand ? shareBrandState === 'error' : shareError) ? 'Could not load the share curve — try again in a moment.'
                    : (perBrand ? shareBrandState === 'loading' : shareLoading) ? 'Loading…'
                      : noSales ? NO_SALES_FOR_SELECTION : 'No share data for this period.'}
                  {(perBrand ? shareBrandState === 'error' : shareError) && <div><button onClick={() => setTrendAttempt((n) => n + 1)}>Try again</button></div>}
                </div>
              )
            })()}
          </div>
        </div>
      </div>

      {/* Notes on the trend sat inside the LEFT card only, which made that card taller than
          the share card beside it and pushed the two x-axes onto different lines (Olof 18/9:
          "den line:ar inte diagrammet på de två"). The notes are scoped to the period, not to
          one curve, so they belong under both. */}
      <div ref={notesRef} className="sticky-anchor">
        <TrendNotes period={period} openAt={noteAt} onLoaded={setNotes} />
      </div>

      </>}
      <div hidden={view !== 'trends'}>
        <ArticleComparison segment={segment} from={range.from} to={range.to} freq={freq} />
      </div>
    </section>
  )
}

// Wrapper: derive the selectable periods from the dos-layer (real months, latest
// first) before mounting the dashboard, so nothing is ever pinned to a hardcoded
// month. The inner component keeps a non-null active period (defaulting to the
// latest), so all its data effects stay simple.
export function LandingHero({
  onPinInsight,
  pinnedInsightIds,
  periods,
  period,
  view = 'overview',
  username,
  onShareCard,
}: {
  onPinInsight: (ins: Insight) => void
  pinnedInsightIds: Set<string>
  periods: Period[]
  period: Period | null
  view?: 'overview' | 'trends'
  username?: string
  onShareCard?: (figure: ShareCardFigure | null) => void
}) {
  // Periods and the selected month come from App now — ONE global control (A4), so this view
  // can no longer disagree with the others about which month is on screen.
  if (!periods.length || !period) {
    return (
      <section style={S.wrap}>
        <div style={S.empty}>
          {periods.length
            ? 'Loading…'
            : 'No completed month yet — figures appear the day after a month closes.'}
        </div>
      </section>
    )
  }
  return (
    <LandingHeroInner
      onPinInsight={onPinInsight}
      pinnedInsightIds={pinnedInsightIds}
      periods={periods}
      period={period}
      view={view}
      username={username}
      onShareCard={onShareCard}
    />
  )
}
